import { BaseService } from './BaseService';
import type {
  ScheduleDto,
  CreateScheduleRequest,
  UpdateScheduleRequest,
  ScheduleFilters,
  ScheduleExecutionFilters,
  ScheduleExecutionDto,
} from '../types/schedule';
import type { PagedResult } from '../types/tenant';

/**
 * Schedule service for handling schedule operations.
 */
export class ScheduleService extends BaseService {
  /**
   * Gets a paginated list of schedules with optional filtering.
   */
  async getSchedules(filters?: ScheduleFilters): Promise<PagedResult<ScheduleDto>> {
    const queryParams = new URLSearchParams();
    if (filters?.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters?.searchTerm) queryParams.append('searchTerm', filters.searchTerm);
    if (filters?.agentDefinitionId) queryParams.append('agentDefinitionId', filters.agentDefinitionId);
    if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());

    const url = `/schedules${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<ScheduleDto>>(url);
  }

  /**
   * Gets a schedule by ID.
   */
  async getSchedule(id: string): Promise<ScheduleDto> {
    return this.get<ScheduleDto>(`/schedules/${id}`);
  }

  /**
   * Creates a new schedule.
   */
  async createSchedule(data: CreateScheduleRequest): Promise<ScheduleDto> {
    return this.post<ScheduleDto>('/schedules', data);
  }

  /**
   * Updates an existing schedule.
   */
  async updateSchedule(id: string, data: UpdateScheduleRequest): Promise<ScheduleDto> {
    return this.put<ScheduleDto>(`/schedules/${id}`, data);
  }

  /**
   * Deletes a schedule.
   */
  async deleteSchedule(id: string): Promise<void> {
    return this.delete<void>(`/schedules/${id}`);
  }

  /**
   * Enables a schedule.
   */
  async enableSchedule(id: string): Promise<ScheduleDto> {
    return this.post<ScheduleDto>(`/schedules/${id}/enable`);
  }

  /**
   * Disables a schedule.
   */
  async disableSchedule(id: string): Promise<ScheduleDto> {
    return this.post<ScheduleDto>(`/schedules/${id}/disable`);
  }

  /**
   * Gets execution history for a schedule.
   */
  async getScheduleExecutions(
    scheduleId: string,
    filters?: ScheduleExecutionFilters
  ): Promise<PagedResult<ScheduleExecutionDto>> {
    const queryParams = new URLSearchParams();
    if (filters?.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters?.status) queryParams.append('status', filters.status);

    const url = `/schedules/${scheduleId}/executions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<ScheduleExecutionDto>>(url);
  }
}

export const scheduleService = new ScheduleService();
